import { ReactNode, useEffect, useId, useState } from "react";
import { ClickAwayListener, SvgIcon, Tooltip, TooltipProps } from "@mui/material";
import { CoreIconButton } from "../button/Button";
import { isMobileDevice } from "../../util";
import i18nPackage from "../../i18n/i18n";
import styles from "./InfoIndicator.module.css";
import Info from "./images/info.svg?react";

export type CoreInfoIndicatorProps = {
	hint: ReactNode;
	size?: number;
	color?: string;
} & Omit<TooltipProps, "title" | "children" | "id">;

export default function CoreInfoIndicator({ hint, size, color = "#42296e", ...rest }: CoreInfoIndicatorProps) {
	const tooltipId = useId();
	const isTouchScreen = isMobileDevice();
	const [tooltipOpen, setTooltipOpen] = useState(false);

	useEffect(() => {
		if (!isTouchScreen || !tooltipOpen) return;

		const handleScroll = () => setTooltipOpen(false);
		window.addEventListener("scroll", handleScroll, true);

		return () => window.removeEventListener("scroll", handleScroll, true);
	}, [isTouchScreen, tooltipOpen]);

	if (isTouchScreen)
		return (
			<ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
				<Tooltip {...rest} id={tooltipId} title={hint} open={tooltipOpen} describeChild>
					<CoreIconButton
						variant="plain"
						className={styles.indicatorButton}
						onClick={() => setTooltipOpen((prev) => !prev)}
						aria-describedby={tooltipId}
					>
						<SvgIcon sx={{ fontSize: size, color: color }} aria-label={i18nPackage.t("coreComponents.infoIndicator.moreInfoAriaLabel")}>
							<Info />
						</SvgIcon>
					</CoreIconButton>
				</Tooltip>
			</ClickAwayListener>
		);

	return (
		<Tooltip {...rest} id={tooltipId} title={hint} describeChild>
			<SvgIcon
				className={styles.indicator}
				tabIndex={0}
				focusable="true"
				aria-label={i18nPackage.t("coreComponents.infoIndicator.moreInfoAriaLabel")}
				aria-describedby={tooltipId}
				aria-hidden={undefined}
				sx={{ fontSize: size, color: color }}
			>
				<Info />
			</SvgIcon>
		</Tooltip>
	);
}
